/**
 * Shape helpers of the generator, ported from the prototype: blob shapes, the zoo plot, the entrance,
 * main loops and pushing points out of a lake. All take an explicit seeded rng.
 */
import type { Pt } from '../../shared/types';
import { centroid, chaikin, CHAIKIN_ITERS, clamp, distPoly, nearestIdx, pip, polyArea } from '../geom';
import { vnoise, type Rng } from '../raster';

/**
 * A wobbly blob of `k` corners around (cx, cy) whose smoothed outline (Chaikin, as areas are drawn) covers
 * `area` m².
 */
export function blob(r: Rng, cx: number, cy: number, area: number, k = 10, wobble = 0.36): Pt[] {
  const rot = r() * Math.PI * 2, asp = 0.78 + r() * 0.44, base = Math.sqrt(area / Math.PI), pts: Pt[] = [];
  for (let j = 0; j < k; j++) {
    const a = (j + (r() - 0.5) * 0.35) / k * Math.PI * 2, rad = base * (1 - wobble / 2 + r() * wobble);
    const x = Math.cos(a) * rad * asp, y = Math.sin(a) * rad / asp;
    pts.push([x * Math.cos(rot) - y * Math.sin(rot), x * Math.sin(rot) + y * Math.cos(rot)]);
  }
  const f = Math.sqrt(area / (polyArea(chaikin(pts, null, CHAIKIN_ITERS)) || 1));
  return pts.map(p => [cx + p[0] * f, cy + p[1] * f]);
}

/** The zoo boundary corners for a generated shape, and whether it is drawn smoothed. */
export function makePlot(r: Rng, shape: 'blob' | 'square' | { ring: Pt[] }, M: number, seed: number): { pts: Pt[]; smooth: boolean } {
  if (typeof shape === 'object' && shape.ring.length >= 3) return { pts: shape.ring.map(p => [p[0], p[1]]), smooth: false };
  if (shape === 'square') return { pts: [[8, 8], [M - 8, 8], [M - 8, M - 8], [8, M - 8]], smooth: false };
  const k = 14, asp = 0.88 + r() * 0.24, ph = r() * 50, pts: Pt[] = [];
  for (let j = 0; j < k; j++) {
    const a = j / k * Math.PI * 2, rad = M * 0.46 * (0.84 + 0.2 * vnoise(Math.cos(a) * 1.6 + ph, Math.sin(a) * 1.6 + ph, seed) + 0.06 * r());
    pts.push([clamp(M / 2 + Math.cos(a) * rad * asp, 10, M - 10), clamp(M / 2 + Math.sin(a) * rad / asp, 10, M - 10)]);
  }
  return { pts, smooth: true };
}

/** The entrance: the boundary point on the lower half nearest the middle, facing the plot's centre. */
export function placeEntrance(pp: readonly Pt[], M: number): { x: number; y: number; ix: number; iy: number } {
  const C = centroid(pp);
  let best = pp[0] as Pt, bd = 1e9;
  for (const p of pp) if (p[1] > C[1]) { const d = Math.abs(p[0] - M / 2); if (d < bd) { bd = d; best = p; } }
  const dx = C[0] - best[0], dy = C[1] - best[1], l = Math.hypot(dx, dy) || 1;
  return { x: best[0], y: best[1], ix: dx / l, iy: dy / l };
}

/** True when p is inside the polygon and at least m metres from its edge. */
export function insideWithMargin(pp: readonly Pt[], p: Pt, m: number): boolean {
  return pip(pp, p[0], p[1]) && distPoly(pp, p[0], p[1], true) >= m;
}

/** Distance from c in direction (ux, uy) until leaving the polygon (2 m steps). */
export function leaveDist(poly: readonly Pt[], c: Pt, ux: number, uy: number): number {
  let d = 0;
  while (d < 4000 && pip(poly, c[0] + ux * d, c[1] + uy * d)) d += 2;
  return d;
}

/**
 * Control points of a main loop around c (radii rx, ry), pulled inside the plot margin, with the point nearest
 * the plaza moved onto the plaza centre. Returns the points and the index of the plaza point.
 */
export function makeLoop(r: Rng, c: Pt, rx: number, ry: number, pp: readonly Pt[], margin: number, plazaC: Pt): { pts: Pt[]; plazaIdx: number } {
  const k = 10, pts: Pt[] = [];
  for (let j = 0; j < k; j++) {
    const a = (j + (r() - 0.5) * 0.3) / k * Math.PI * 2, f = 0.86 + r() * 0.24;
    let p: Pt = [c[0] + Math.cos(a) * rx * f, c[1] + Math.sin(a) * ry * f];
    for (let t = 0; t < 80 && !insideWithMargin(pp, p, margin); t++) p = [p[0] + (c[0] - p[0]) * 0.06, p[1] + (c[1] - p[1]) * 0.06];
    pts.push(p);
  }
  const [ni] = nearestIdx(pts, plazaC[0], plazaC[1]);
  pts[ni] = [plazaC[0], plazaC[1]];
  return { pts, plazaIdx: ni };
}

/** Moves p (in place) away from the polygon's centre until it is outside it and `gap` metres clear. */
export function pushOutOf(p: Pt, poly: readonly Pt[], gap: number): Pt {
  const c = centroid(poly);
  for (let t = 0; t < 120; t++) {
    if (!pip(poly, p[0], p[1]) && distPoly(poly, p[0], p[1], true) >= gap) break;
    const dx = p[0] - c[0], dy = p[1] - c[1], l = Math.hypot(dx, dy) || 1;
    p[0] += dx / l * 2.5; p[1] += dy / l * 2.5;
  }
  return p;
}
